import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { Chip } from '@/UI/Chip/Chip';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import CopyBtn from '@/components/Common/ImageList/CopyBtnV2';
import { useIsHovered } from '@/hooks/useIsHovered';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { CopyIcon } from '@/UI/Icons/CopyIcon';
import { ImageIcon } from '@/UI/Icons/ImageIcon';
import type { ApiObject } from '@/types/analytics.types';

const getFormattedParam = (param: string) => {
  return param
    .split('_') // Split by underscore
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(' ');
};

const isValidParamValue = (value: unknown): boolean => {
  const typeofValue = typeof value;
  return !(typeofValue === 'object' || value === '' || typeofValue === 'undefined' || typeofValue === 'function');
};

// Style constants
const TYPOGRAPHY_STYLES = {
  italic: { fontStyle: 'italic', fontWeight: '400' },
  copyButton: { position: 'absolute', top: 2, right: 2 },
  /** Keeps a long `param: value` chip label on one line and elides the overflow. */
  truncated: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
} as const;

interface CopyButtonProps {
  text: string;
  trackingName: string;
  trackingPayload?: ApiObject;
  size?: 'medium' | 'large';
  onClick?: () => void;
}

const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  trackingName,
  trackingPayload = {},
  size = 'medium',
  onClick,
}) => {
  const { copyToClipboard } = useCopyToClipboard({
    trackingName,
    trackingPayload,
  });

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      void copyToClipboard(text);
    }
  };

  return (
    <CopyBtn
      text={text}
      size={size}
      onClick={handleClick}
      eventTracking={{ name: trackingName, payload: trackingPayload }}
    />
  );
};

const ExpandableText = ({ text, maxChars = 300 }: { text: string; maxChars?: number }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const displayText = useMemo(() => {
    if (text.length <= maxChars || expanded) {
      return text;
    }

    return text.slice(0, maxChars).trim() + '...';
  }, [text, maxChars, expanded]);

  const handleToggle = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setExpanded(!expanded);
  };

  return (
    <FlexCenVer sx={{ gap: 0.25 }}>
      <TextSmRg sx={TYPOGRAPHY_STYLES.italic}>{displayText}</TextSmRg>
      {text.length > maxChars ? (
        <TextSmRg sx={TYPOGRAPHY_STYLES.italic}>
          <a
            href="#"
            onClick={handleToggle}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              ...TYPOGRAPHY_STYLES.italic,
            }}
          >
            {expanded
              ? t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.SHOW_LESS)
              : t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.SHOW_MORE)}
          </a>
        </TextSmRg>
      ) : null}
    </FlexCenVer>
  );
};

const CustomChip = ({ param, value }: { param: string; value: string }) => {
  const { t } = useTranslation();
  const formattedParam = getFormattedParam(param);
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const { copied, copyToClipboard } = useCopyToClipboard({
    trackingName: 'gallery_params_copied_param',
    trackingPayload: { param },
    timeout: 500,
  });

  // The chip label is capped and elided, so hovering reveals the untruncated `param: value`.
  // The tooltip stays fully controlled — `copied` takes precedence so the copy confirmation
  // still replaces the text while the pointer is on the chip.
  return (
    <Tooltip
      title={copied ? t(I18N_KEYS.GENERAL.COPIED) : `${formattedParam}: ${value}`}
      open={copied || isHovered}
      disableHoverListener
      placement="top"
    >
      <Chip
        size="small"
        sx={{ width: 'fit-content', maxWidth: '100%', px: 0.5 }}
        label={
          <TextSmRg sx={TYPOGRAPHY_STYLES.truncated}>
            {formattedParam}: <b>{value}</b>
          </TextSmRg>
        }
        onClick={() => void copyToClipboard(value)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    </Tooltip>
  );
};

const isUrl = (value: string): boolean => value.startsWith('http://') || value.startsWith('https://');

const CHIP_WIDTH = 86;

const ParamDisplay = ({ param, value }: { param: string; value: string }) => {
  const formattedParam = getFormattedParam(param);
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();

  return (
    <FlexCenVer
      sx={{ height: '24px', width: '100%', justifyContent: 'space-between', gap: 1 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* `flex: 1, minWidth: 0` lets a long param name shrink below its content width so the
          ellipsis can engage instead of spilling over the value chip. */}
      <FlexCenVer sx={{ flex: 1, minWidth: 0 }}>
        {/* EllipsisText tooltips the full name on hover, but only when it is actually truncated.
            Uses the default 100ms delay so it matches the value chip in the same row. */}
        <EllipsisText variant="body-sm-rg">{formattedParam}</EllipsisText>
      </FlexCenVer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '24px',
          width: `${CHIP_WIDTH}px`,
          flex: `0 0 ${CHIP_WIDTH}px`,
          gap: '4px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            height: '24px',
            px: 1,
            overflow: 'hidden',
            bgcolor: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-medium)',
          }}
        >
          <TextSmRg
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'var(--color-text)',
            }}
          >
            {value}
          </TextSmRg>
        </Box>
        {isHovered && (
          <CopyButton
            text={value}
            trackingName="gallery_params_copied_param"
            trackingPayload={{ param }}
            size="medium"
          />
        )}
      </Box>
    </FlexCenVer>
  );
};

const PlaceholderImage = () => {
  const { t } = useTranslation();

  return (
    <Tooltip title={t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.NO_PREVIEW)} placement="bottom">
      <FlexCenVer
        sx={{
          width: '56px',
          height: '56px',
          borderRadius: 1,
          bgcolor: color.White04_T,
          justifyContent: 'center',
        }}
      >
        <ImageIcon width="24" height="24" color={token.textSecondary} />
      </FlexCenVer>
    </Tooltip>
  );
};

const SortedParams = ({
  params,
  RenderComponent,
  header = null,
}: {
  params?: Record<string, unknown>;
  header?: React.ReactNode;
  RenderComponent: ({ param, value }: { param: string; value: string }) => React.JSX.Element;
}) => {
  if (!params) return null;
  const filteredParams = Object.entries(params).filter(([param, _]) => param !== 'file');
  if (filteredParams.length === 0) return null;
  // Values may contain '\n'-separated entries (e.g. arrays joined by validateInputInfo),
  // so split them into individual render items.
  const mappedParams = filteredParams.flatMap(([param, value]) => {
    if (!isValidParamValue(value)) {
      return [];
    }

    const strValue = typeof value === 'boolean' ? value.toString() : String(value);
    const lines = strValue.split('\n').filter(Boolean);
    if (lines.length > 1) {
      return lines.map((line, i) => <RenderComponent key={`${param}_${i}`} param={`${param}_${i + 1}`} value={line} />);
    }

    return [<RenderComponent key={param} param={param} value={strValue} />];
  });
  if (mappedParams.length === 0) return null;
  return (
    <FlexCol sx={{ gap: 1 }}>
      {header}
      {mappedParams}
    </FlexCol>
  );
};

const CopyIndication = ({ copied, copyToClipboard }: { copied: boolean; copyToClipboard: () => void }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t(I18N_KEYS.GENERAL.COPIED)} open={copied} disableHoverListener placement="top">
      <Box id="gallery-params-copy-n-prompt" style={TYPOGRAPHY_STYLES.copyButton}>
        {!copied ? (
          <CopyIcon
            className="fade-in-slow"
            width={16}
            height={16}
            style={{ cursor: 'pointer' }}
            onClick={copyToClipboard}
          />
        ) : (
          <CheckMarkIcon className="fade-in-slow" width={16} height={16} />
        )}
      </Box>
    </Tooltip>
  );
};

export {
  CopyButton,
  ExpandableText,
  CustomChip,
  ParamDisplay,
  PlaceholderImage,
  SortedParams,
  CopyIndication,
  getFormattedParam,
  isUrl,
};
